/**
 * This module provides functions in relation to calling API routes on the backend server and processing the incoming
 * responses. The functions in this module refer to the API endpoint ApiEndPoints.FORMULA.
 * @module
 */

import {ApiEndPoints, HttpMethods} from "../base/Settings";
import type {FetchRequest, FormulaInput, Formula, UUID} from "../base/ui_general/GeneralInterfaces";


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the creation of a new
 * formula on the server. Only name, description and formula type need to be specified for this purpose.
 * @param formulaInput An object of type FormulaInput containing the input data for creating the formula.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_create_formula(formulaInput: FormulaInput): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.FORMULA,
        method: HttpMethods.POST,
        query_parameters: {},
        body: formulaInput,
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for updating an existing
 * formula on the server.
 * @param formula An object of type Formula containing the updated formula data.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_update_formula(formula: Formula): FetchRequest {
    const formulaPayload = Object.fromEntries(
        Object.entries(formula).filter(([key]) => key !== "formula_rows")
    );
    return {
        api_endpoint: ApiEndPoints.FORMULA,
        method: HttpMethods.PUT,
        query_parameters: {},
        body: formulaPayload,
    };
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for triggering the query of formulas
 * on the server.
 * @param transaction_id .The UUID of the transaction to query for.
 * @returns A FetchRequest object containing the generated fetch request.*/
export function getRequest_get_all_formulas(transaction_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.FORMULA,
        method: HttpMethods.GET,
        query_parameters: { transaction_id: transaction_id },
    }
}


/**
 * Creates a FetchRequest object populated with the parameters necessary for deleting an existing
 * formula on the server.
 * @param formula_id The UUID of the formula to delete.
 * @returns A FetchRequest object containing the generated fetch request.
 */
export function getRequest_delete_formula(formula_id: UUID): FetchRequest {
    return {
        api_endpoint: ApiEndPoints.FORMULA,
        method: HttpMethods.DELETE,
        query_parameters: { formula_id: formula_id },
    };
}
